import { useState, useEffect, useMemo } from "react";

//Prevents new reference
const EMPTY_ARRAY = [];

// This will get the value from the object by the path
// example: getByPath({ nameI18n: { en: "Hello" } }, "nameI18n.en")
// will return "Hello"
export const getByPath = (obj, path) =>
  path?.split(".").reduce((o, p) => (o == null ? o : o[p]), obj);

// This will set the value in the object by the path
// example: setByPath({ nameI18n: { en: "Hello" } }, "nameI18n.en", "Hello")
// will return { nameI18n: { en: "Hello" } }
// Only the objects along the path are cloned, everything else keeps its reference
export const setByPath = (obj, path, value) => {
  const parts = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    cur[p] = typeof cur[p] === "object" && cur[p] !== null ? { ...cur[p] } : {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
};

export const useInlineTableEdit = (data) => {
  const stableData = useMemo(() => data ?? EMPTY_ARRAY, [data]);
  const [tableData, setTableData] = useState(stableData);

  const [isAddingRows, setIsAddingRows] = useState(false);
  const [editingRowId, setEditingRowId] = useState(false);
  const [errors, setErrors] = useState({});

  // Synchronize the table data, only if not adding rows or editing a row
  // We want to avoid unnecessary re-renders when you are editing a row
  // Which causes the row / data loss and unnecessary re-renders
  useEffect(() => {
    if (!isAddingRows && !editingRowId) {
      setTableData(stableData);
    }
  }, [stableData, isAddingRows, editingRowId]);

  // This will add new empty row to the table
  // when clicked on the add button
  const handleAddNewRow = (createEmptyRow) => {
    // Prevent adding multiple rows at once
    if (isAddingRows) return;

    setIsAddingRows(true);
    const newRow = createEmptyRow();
    setTableData([newRow, ...tableData]);
  };

  // This will update the cell value in the table
  const handleCellChange = (id, column, value) => {
    const updatedData = tableData.map((row) =>
      row.id === id ? setByPath(row, column, value) : row
    );
    setTableData(updatedData);
  };

  const startEditing = (id) => {
    setIsAddingRows(true);
    setEditingRowId(id);
    setErrors({});
  };

  const cancelEditing = () => {
    setIsAddingRows(false);
    setEditingRowId(null);
    setErrors({});

    setTableData(stableData);
  };

  // This will remove the row from the table
  // when clicked on the cancel button
  const cancelRowAdd = (id) => {
    setTableData((prev) => prev.filter((row) => row.id !== id));
    setIsAddingRows(false);
    setErrors({});
  };

  return {
    tableData,
    setTableData,
    isAddingRows,
    editingRowId,
    errors,
    setErrors,
    handleAddNewRow,
    handleCellChange,
    startEditing,
    cancelEditing,
    cancelRowAdd,
    onSave: () => {
      setIsAddingRows(false);
      setEditingRowId(null);
    },
  };
};
